import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import Logo from "@img/logo.png";

axios.defaults.withCredentials = true;

const TABS = [
  { label: "Задачи", to: "/" },
  { label: "Аккаунты", to: "/accounts" },
  { label: "Прокси", to: "/proxy" },
];

function getActiveTo(pathname) {
  if (pathname === "/") return "/";
  if (pathname.startsWith("/accounts")) return "/accounts";
  if (pathname.startsWith("/proxy")) return "/proxy";
  return "/";
}

export default function Header() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const activeTo = useMemo(() => getActiveTo(pathname), [pathname]);

  const navRef = useRef(null);
  const itemRefs = useRef({});
  const [pill, setPill] = useState({ left: 4, width: 160 });
  const [loggingOut, setLoggingOut] = useState(false);

  const recalc = () => {
    const nav = navRef.current;
    const el = itemRefs.current[activeTo];
    if (!nav || !el) return;

    setPill({
      left: el.offsetLeft,
      width: el.offsetWidth,
    });
  };

  useEffect(() => {
    recalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTo]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const ro = new ResizeObserver(() => recalc());
    ro.observe(nav);

    const onResize = () => recalc();
    window.addEventListener("resize", onResize);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTo]);

  const onLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await axios.post("/api/auth/logout");
    } catch (e) {
      // даже если ошибка — всё равно выкидываем
      console.error("logout error:", e);
    } finally {
      // важное: replace чтобы назад не вернуться в защищенную часть
      navigate("/login", { replace: true });
      setLoggingOut(false);
    }
  };

  return (
    <header className="w-full px-6 pt-3">
      <div className="mx-auto max-w-[1220px]">
        <div className="bg-white rounded-[999px] border border-[#C8C8C8] shadow-[0_10px_30px_rgba(0,0,0,0.06)] px-10 py-3 flex items-center justify-between">
          {/* Left: Logo */}
          <Link to="/" className="flex items-center gap-3">
            <div className="flex items-end gap-1">
              <img src={Logo} width="110px" alt="Логотип" />
            </div>
          </Link>

          {/* Center: Tabs */}
          <div className="flex items-center gap-2 bg-[#F3F4F6] rounded-full p-1">
            <div ref={navRef} className="relative flex items-center gap-2">
              {/* animated pill */}
              <div
                className="absolute top-0 bottom-0 rounded-full bg-[#1F1F1F] shadow-sm transition-all duration-300 ease-out"
                style={{ left: pill.left, width: pill.width }}
              />

              {TABS.map((t) => {
                const active = t.to === activeTo;

                return (
                  <Link
                    key={t.to}
                    to={t.to}
                    ref={(node) => {
                      if (node) itemRefs.current[t.to] = node;
                    }}
                    className={[
                      "relative z-10 h-9 px-4 rounded-full text-sm font-medium transition",
                      "inline-flex items-center justify-center whitespace-nowrap",
                      active ? "text-white" : "text-[#4B5563] hover:bg-black/5",
                    ].join(" ")}
                  >
                    {t.label}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Right: User */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-[#F3F4F6] rounded-full px-3 h-9">
              <span className="text-[#111827]">
                <i className="fa-regular fa-user w-4 h-3" />
              </span>
              <span className="text-sm font-medium text-[#111827]">admin</span>
            </div>

            <button
              onClick={onLogout}
              disabled={loggingOut}
              className="w-9 h-9 rounded-full text-[#EF4444] grid place-items-center hover:bg-[#FFECEC] transition disabled:opacity-50"
              title="Выйти"
              type="button"
            >
              <i className="fa-solid fa-arrow-right-from-bracket" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
